import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FocusLayout from '../components/layouts/FocusLayout';
import TopBar from '../components/common/TopBar';
import BottomMenu from '../components/full_mock_test/attempt/BottomMenu';
import AttemptMainContent from '../components/full_mock_test/attempt/AttemptMainContent';
import LeftPanel from '../components/full_mock_test/attempt/LeftPanel';
import TestSubmissionConfirm from '../components/personalized_tests/attempt/TestSubmissionConfirm';
import TestResultSummary from '../components/personalized_tests/attempt/TestResultSummary';

const PersonalizedTestAttempt = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attemptData, setAttemptData] = useState(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [showSubmissionConfirm, setShowSubmissionConfirm] = useState(false);
  const [showResultSummary, setShowResultSummary] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [currentExerciseData, setCurrentExerciseData] = useState(null);
  const [isFitMode, setIsFitMode] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isGrading, setIsGrading] = useState(false);
  const [sharedColor, setSharedColor] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [fillHintSignal, setFillHintSignal] = useState(0);

  const handleFillHint = () => setFillHintSignal(prev => prev + 1);

  // --- Dynamic Zoom Calculation ---
  useEffect(() => {
    const calculateZoom = () => {
      if (isFitMode && contentRef.current) {
        setTimeout(() => {
          if (!contentRef.current) return;
          const container = contentRef.current;
          const originalZoom = container.style.zoom;
          container.style.zoom = "1";

          const scrollHeight = container.scrollHeight;
          const clientHeight = container.clientHeight;

          if (scrollHeight > clientHeight) {
            const calculatedZoom = (clientHeight / scrollHeight) * 0.98;
            setZoomLevel(Math.max(0.5, calculatedZoom));
          } else {
            setZoomLevel(1);
          }
          container.style.zoom = originalZoom;
        }, 100);
      } else {
        setZoomLevel(1);
      }
    };

    calculateZoom();
    window.addEventListener('resize', calculateZoom);
    return () => window.removeEventListener('resize', calculateZoom);
  }, [isFitMode, currentExerciseData]);

  const startTest = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/client/personalized-tests/${examId}/start`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: ''
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Sort items by order_index
      if (data.test_detail && data.test_detail.items) {
        data.test_detail.items.sort((a, b) => a.order_index - b.order_index);
      }
      setAttemptData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      // Loading state will be handled by fetchExerciseDetail for the first item
    }
  }, [examId]);

  const fetchExerciseDetail = useCallback(async (exerciseId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/client/exercises/${exerciseId}?mode=test`, {
        headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch exercise details');
      const data = await response.json();
      setCurrentExerciseData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    startTest();
  }, [startTest]);

  useEffect(() => {
    if (attemptData) {
      // Set timer to null if not available, or read from exercise config
      const timeLimit = currentExerciseData?.content_data?.time_limit ? parseInt(currentExerciseData.content_data.time_limit) * 60 : null;
      setTimeLeft(timeLimit);
    }
  }, [attemptData, currentExerciseData]);

  // Countdown effect
  useEffect(() => {
    if (isReviewMode || timeLeft === null || timeLeft <= 0 || showSubmissionConfirm || showResultSummary) {
      return;
    }
    const timerId = setInterval(() => setTimeLeft(time => time - 1), 1000);
    return () => clearInterval(timerId);
  }, [timeLeft, isReviewMode, showSubmissionConfirm, showResultSummary]);

  useEffect(() => {
    if (attemptData && attemptData.test_detail?.items[currentExerciseIndex]) {
      const exerciseId = attemptData.test_detail.items[currentExerciseIndex].exercise.id;
      fetchExerciseDetail(exerciseId);
    }
  }, [attemptData, currentExerciseIndex, fetchExerciseDetail]);

  const handleNext = () => {
    setShowHint(false);
    if (attemptData && currentExerciseIndex < attemptData.test_detail.items.length - 1) {
      setCurrentExerciseIndex(prev => prev + 1);
    } else {
      setShowSubmissionConfirm(true);
    }
  };

  const handlePrev = () => {
    setShowHint(false);
    if (showSubmissionConfirm) {
      setShowSubmissionConfirm(false);
    } else if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(prev => prev - 1);
    }
  };

  const handleReview = () => {
    setShowSubmissionConfirm(false);
  };

  const handleSubmit = async () => {
    if (!attemptData) return;

    const attemptKey = `attempt_${attemptData.attempt_id}`;
    let allSavedAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allSavedAnswers = JSON.parse(savedAttempt);
    } catch (e) {
      console.error("Failed to parse localStorage data:", e);
    }

    const answersToSubmit = [];
    attemptData.test_detail.items.forEach(item => {
      const exerciseId = item.exercise.id;
      const exerciseAnswers = allSavedAnswers[exerciseId];
      if (exerciseAnswers) {
        Object.keys(exerciseAnswers).forEach(questionId => {
          answersToSubmit.push({
            question_id: questionId,
            user_answer: [JSON.stringify(exerciseAnswers[questionId])]
          });
        });
      }
    });

    try {
      setLoading(true);
      setIsGrading(true);
      const token = localStorage.getItem('accessToken');

      // 1. Save Answers
      const saveResponse = await fetch(`${process.env.REACT_APP_API_URL}/client/personalized-tests/attempts/${attemptData.attempt_id}/answers`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers: answersToSubmit })
      });

      if (!saveResponse.ok) {
        throw new Error(`Save answers failed: ${saveResponse.status}`);
      }

      // 2. Submit Attempt
      const submitResponse = await fetch(`${process.env.REACT_APP_API_URL}/client/personalized-tests/attempts/${attemptData.attempt_id}/submit`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: ''
      });

      if (!submitResponse.ok) {
        throw new Error(`Submit attempt failed: ${submitResponse.status}`);
      }

      const submitData = await submitResponse.json();
      setResultData(submitData);

      // Clean up the local storage for this attempt
      localStorage.removeItem(`attempt_${attemptData.attempt_id}`);

      // Show result summary instead of navigating
      setShowSubmissionConfirm(false);
      setShowResultSummary(true);

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setIsGrading(false);
    }
  };

  const handleReviewResults = () => {
    setShowResultSummary(false);
    setIsReviewMode(true);
    setCurrentExerciseIndex(0); // Start review from the beginning
  };

  const handleBackToDashboard = () => {
    navigate('/personalized-tests');
  };

  if (loading) {
    return (
      <FocusLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
        </div>
      </FocusLayout>
    );
  }

  if (error) {
    return (
      <FocusLayout>
        <div className="flex items-center justify-center h-screen p-4 text-red-600">
          Error: {error}
        </div>
      </FocusLayout>
    );
  }

  if (showResultSummary) {
    return (
      <FocusLayout>
        <TestResultSummary
          resultData={resultData}
          onReview={handleReviewResults}
          onBack={handleBackToDashboard}
        />
      </FocusLayout>
    );
  }

  const currentItem = attemptData?.test_detail?.items[currentExerciseIndex];
  // const currentExercise = currentItem?.exercise; // No longer used directly for content, but id is used for fetching

  return (
    <FocusLayout>
      {isGrading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm animate-fade-in">
          <div className="text-center max-w-md px-6">
            <div className="relative mb-8 inline-block">
              <div className="w-24 h-24 rounded-3xl bg-primary-blue/10 dark:bg-blue-500/20 flex items-center justify-center animate-pulse">
                <span className="material-symbols-outlined text-5xl text-primary-blue dark:text-blue-400">
                  assignment_turned_in
                </span>
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center shadow-lg border-4 border-white dark:border-slate-900 animate-bounce">
                <span className="material-symbols-outlined text-white text-xs font-bold">
                  edit
                </span>
              </div>
            </div>
            <h2 className="text-3xl font-display font-black text-slate-800 dark:text-white mb-4">
              Giáo viên đang chấm bài...
            </h2>
            <p className="text-slate-500 dark:text-slate-400 font-body leading-relaxed mb-8">
              Vui lòng chờ trong giây lát. Hệ thống AI đang phân tích bài làm của em để đưa ra kết quả và nhận xét chính xác nhất.
            </p>
            <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 mb-2 overflow-hidden relative">
              <div className="absolute top-0 bottom-0 bg-primary-blue rounded-full animate-loading-bar" />
            </div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 animate-pulse">
              Processing results with AI Examiner
            </p>
          </div>
        </div>
      )}
      <div
        className="flex h-screen overflow-hidden justify-center items-center"
        style={{
          background: 'linear-gradient(135deg, #6b4f2a 0%, #3d2b0f 50%, #6b4f2a 100%)',
          padding: '16px 12px',
        }}
      >
        <div
          className="flex w-full h-full relative"
          style={{
            maxWidth: '100%',
            filter: 'drop-shadow(0 20px 60px rgba(0,0,0,0.6))',
            borderRadius: '4px 4px 4px 4px',
          }}
        >
          {/* Màn hình Review Submission / Full width khi Confirm */}
          {showSubmissionConfirm ? (
            <div className="w-full h-full bg-white dark:bg-card-dark rounded-md overflow-hidden relative z-10">
              <TestSubmissionConfirm
                onReview={handleReview}
                onSubmit={handleSubmit}
              />
            </div>
          ) : (
            <>
              {/* ===== LEFT PAGE (Sidebar) ===== */}
              <div
                className="hidden md:flex flex-col w-1/2 shrink-0 h-full"
                style={{
                  background: 'linear-gradient(to right, #f5ead6 0%, #fdf6e3 85%, #f0e4c8 100%)',
                  borderRadius: '6px 0 0 6px',
                  boxShadow: 'inset -8px 0 20px rgba(0,0,0,0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Page texture lines */}
                <div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
                  backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, rgba(180,160,120,0.15) 31px, rgba(180,160,120,0.15) 32px)',
                  backgroundPositionY: '48px',
                }} />
                {/* Left margin red line */}
                <div style={{
                  position: 'absolute', top: 0, bottom: 0, left: '48px', width: '1px',
                  background: 'rgba(220,100,100,0.25)', zIndex: 0, pointerEvents: 'none',
                }} />

                {/* Sidebar content */}
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1" style={{ position: 'relative', zIndex: 1, padding: '8px' }}>
                  <LeftPanel
                    examTitle={attemptData?.test_detail?.title || "Personalized Test"}
                    sectionTitle={currentExerciseData?.content_data?.part_label || `Part ${currentExerciseIndex + 1}`}
                    timeLeft={timeLeft}
                    audioUrl={currentExerciseData?.content_data?.audio_url}
                    isStrictAudio={!isReviewMode}
                    partLabel={currentExerciseData?.content_data?.part_label}
                    questionCountLabel={currentExerciseData?.content_data?.question_count_label}
                    instruction={currentExerciseData?.content_data?.instruction}
                    storyText={currentExerciseData?.content_data?.story_text}
                    imageUrl={
                      ['reading_writing_flyer_part_5'].includes(currentExerciseData?.type)
                        ? currentExerciseData?.content_data?.image_url
                        : null
                    }
                    sharedOptions={currentExerciseData?.type === 'listen_flyer_part_3' ? currentExerciseData?.content_data?.shared_options : null}
                    colorOptions={currentExerciseData?.type === 'listen_flyer_part_5' ? currentExerciseData?.questions?.filter(q => !q.question_text?.toLowerCase().startsWith('input')).map(q => q.question_text) : null}
                    inputQuestions={currentExerciseData?.type === 'listen_flyer_part_5' ? currentExerciseData?.questions?.filter(q => q.question_text?.toLowerCase().startsWith('input')) : null}
                    optionBank={
                      currentExerciseData?.type === 'reading_writing_flyer_part_4' ? currentExerciseData?.content_data?.option_bank
                        : null
                    }
                    optionBankExampleAnswers={
                      currentExerciseData?.type === 'reading_writing_flyer_part_4' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers
                        : null
                    }
                    wordBank={
                      currentExerciseData?.type === 'reading_writing_flyer_part_1' ? currentExerciseData?.content_data?.word_bank
                        : currentExerciseData?.type === 'reading_writing_flyer_part_3' ? currentExerciseData?.content_data?.word_bank
                          : null
                    }
                    wordBankExampleAnswers={
                      currentExerciseData?.type === 'reading_writing_flyer_part_1' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers
                        : currentExerciseData?.type === 'reading_writing_flyer_part_3' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers
                          : null
                    }
                    answerBank={currentExerciseData?.type === 'reading_writing_flyer_part_2' ? currentExerciseData?.content_data?.answer_bank : null}
                    answerBankExampleAnswers={currentExerciseData?.type === 'reading_writing_flyer_part_2' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers : null}
                    sharedColor={sharedColor}
                    setSharedColor={setSharedColor}
                  />
                </div>

                {/* Bottom menu on left page */}
                <div
                  className="shrink-0 p-2"
                  style={{
                    position: 'relative', zIndex: 1,
                    background: 'linear-gradient(to top, rgba(240,225,195,0.95), transparent)',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <BottomMenu
                    onPrev={handlePrev}
                    onNext={handleNext}
                    onToggleHint={() => setShowHint(!showHint)}
                    onFillHint={handleFillHint}
                    showHint={showHint}
                    isReviewMode={isReviewMode}
                  />
                </div>
              </div>

              {/* ===== BOOK SPINE (center gutter) ===== */}
              <div
                style={{
                  width: '18px',
                  flexShrink: 0,
                  background: 'linear-gradient(to right, #5a3a10, #8b6020 20%, #c49a40 45%, #e8c870 50%, #c49a40 55%, #8b6020 80%, #5a3a10)',
                  boxShadow: '0 0 18px rgba(0,0,0,0.5)',
                  position: 'relative',
                  zIndex: 2,
                }}
              />

              {/* ===== RIGHT PAGE (Exercise) ===== */}
              <div
                className="flex flex-col w-1/2 h-full"
                style={{
                  background: 'linear-gradient(to left, #f5ead6 0%, #fdf6e3 85%, #f0e4c8 100%)',
                  borderRadius: '0 6px 6px 0',
                  boxShadow: 'inset 8px 0 20px rgba(0,0,0,0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Page texture lines */}
                <div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
                  backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, rgba(180,160,120,0.15) 31px, rgba(180,160,120,0.15) 32px)',
                  backgroundPositionY: '48px',
                }} />

                {/* Mobile TopBar */}
                <div className="md:hidden border-b border-amber-200" style={{ position: 'relative', zIndex: 1 }}>
                  <TopBar
                    examTitle={attemptData?.test_detail?.title || "Personalized Test"}
                    sectionTitle={currentExerciseData?.content_data?.part_label || `Part ${currentExerciseIndex + 1}`}
                    timeLeft={timeLeft}
                    isFitMode={isFitMode}
                    audioUrl={currentExerciseData?.content_data?.audio_url}
                    isStrictAudio={!isReviewMode}
                  />
                  <div className="p-4 text-center bg-amber-50">
                    {currentExerciseData?.content_data?.part_label && (
                      <span className="inline-block px-3 py-1 rounded-full bg-orange-500 text-white font-bold text-xs">
                        {currentExerciseData.content_data.part_label}
                      </span>
                    )}
                    {currentExerciseData?.content_data?.instruction && (
                      <p className="mt-2 text-sm text-slate-700 font-semibold">{currentExerciseData.content_data.instruction}</p>
                    )}
                  </div>
                </div>

                <div
                  ref={contentRef}
                  className="flex-1 overflow-hidden flex flex-col p-2"
                  style={isFitMode ? { zoom: zoomLevel, position: 'relative', zIndex: 1 } : { position: 'relative', zIndex: 1 }}
                >
                  <div className="bg-gradient-to-b from-amber-50 to-white dark:from-slate-800 dark:to-card-dark rounded-2xl shadow-sm border-2 border-amber-100 dark:border-slate-700 p-3 flex flex-col flex-1 h-full min-h-0 relative">
                    {currentExerciseData && (
                      <div className={`flex-1 ${currentExerciseData?.type?.includes('listen') ? 'overflow-hidden flex flex-col min-h-0' : 'overflow-y-auto custom-scrollbar'}`}>
                        <AttemptMainContent
                          exercise={currentExerciseData}
                          attemptId={attemptData.attempt_id}
                          isReviewMode={isReviewMode}
                          showHint={showHint}
                          fillHintSignal={fillHintSignal}
                          isFitMode={isFitMode}
                          zoomLevel={zoomLevel}
                          sharedColor={sharedColor}
                          setSharedColor={setSharedColor}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:hidden shrink-0 z-10 border-t border-amber-200" style={{ position: 'relative', zIndex: 1 }}>
                  <BottomMenu
                    onPrev={handlePrev}
                    onNext={handleNext}
                    onToggleHint={() => setShowHint(!showHint)}
                    onFillHint={handleFillHint}
                    showHint={showHint}
                    isReviewMode={isReviewMode}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </FocusLayout>
  );
};

export default PersonalizedTestAttempt;
